import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { FiEdit3, FiTrash } from 'react-icons/fi';
import { toastSuccess } from '../utils/toasts';
import { RootState, useGetOneSellerSummaryQuery } from '../store';
import TicketsSummaryTable from './SellersTicketSummary';
import ApprovedOrdersTable from './ApprovedOrdersTable';
import parseOrderToOrderItem from '../utils/parseOrderToOrderItem';
import OrderItem from '../types/OrderItem';
import useGetUserRole from '../hooks/useGetUserRole';
import useGetParams from '../hooks/useGetParams';
import TransactionsTable from './TransactionsTable';
import CustomModal from './atoms/CustomModal';
import AddUserExpense from './AddUserExpense';
import AdminSectionTitle from './AdminSectionTitle';
import AddEditSellerToEvent from './AddEditSellerToEvent';
import { SellerSummaryDTO } from '../models/order';
import {
  useGetEventByIdQuery,
} from '../store/apis/events';
import { formatCurrency } from '../utils/shared';
import LoadingLottie from './atoms/LoadingLottie';
import { ordersApi } from '../store/apis/orders';
import Summary from './Summary';
import { EventTransactionType } from '../models/event-transaction';
import { useDownloadQR } from '../utils/sellerUtils';
import DeleteSeller from './DeleteSeller';
import QrCode02Icon from './icons/QrCode02Icon';

const SellersSummaryDetails: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const role = useGetUserRole();
  const handleDownloadQR = useDownloadQR();
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { seller_id } = useParams();
  const { businessId, eventId } = useGetParams();
  const { data: event } = useGetEventByIdQuery({ eventId });
  const userData = useSelector((state: RootState) => state.auth.user);
  const [summary, setSummary] = useState<SellerSummaryDTO | null>(
    null,
  );
  const [orders, setOrders] = useState<OrderItem[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState<'delete' | 'edit' | 'expense'>('delete');
  const [isUserActive, setIsUserActive] = useState(false);
  const [summaryToEdit, setSummaryToEdit] = useState<SellerSummaryDTO | null>(null);
  const sellerId = role === 'SELLER' ? userData?.id : seller_id;
  const { data: refreshedSummary, isLoading: isRefreshedSummaryLoading } = useGetOneSellerSummaryQuery(
    {
      businessId,
      eventId,
      sellerId: sellerId ?? seller_id ?? '',
    },
  );

  useEffect(() => {
    if (refreshedSummary) {
      setSummary(refreshedSummary);
      setIsUserActive(refreshedSummary.user?.current_event?.active || false);
    }
  }, [refreshedSummary, dispatch, seller_id]);

  useEffect(() => {
    if (summary) {
      setOrders(parseOrderToOrderItem(summary.seller_orders));
    }
  }, [summary]);

  if (isRefreshedSummaryLoading) {
    return <LoadingLottie />;
  }

  if (!summary) {
    if (role === 'SELLER') {
      return <LoadingLottie />;
    }
    return <div>no hay reporte</div>;
  }

  const handleDeleteConfirm = () => {
    setShowModal(true);
    setModalType('delete');
  };

  const handleDeleteSuccess = () => {
    toastSuccess('Vendedor eliminado con éxito');
    navigate('..');
  };

  const handleEditUserSuccess = () => {
    setShowModal(false);
    dispatch(ordersApi.util.invalidateTags(['seller_summary']));
    setSummary(null);
  };

  const handleEditUser = () => {
    setSummaryToEdit(summary);
    setShowModal(true);
    setModalType('edit');
  };

  const mainButtons = [
    ...((role === 'ADMIN' || role === 'ROOT')
      ? [
        {
          label: 'Editar vendedor',
          onClick: () => handleEditUser(),
          variant: 'secondary' as const,
          icon: <FiEdit3 className='w-5 h-5' />,
        },
      ]
      : []),
    ...(event?.has_seller_qr === true
      && (role === 'ADMIN' || role === 'ROOT')
      ? [
        {
          label: 'QR vendedor',
          onClick: () => handleDownloadQR(summary, businessId, eventId),
          variant: 'secondary' as const,
          icon: <QrCode02Icon className='w-5 h-5' />,
        },
      ]
      : []),
    ...((role === 'ADMIN' || role === 'ROOT')
      ? [
        {
          label: 'Eliminar vendedor',
          onClick: () => handleDeleteConfirm(),
          variant: 'secondary' as const,
          icon: <FiTrash className='w-5 h-5' />,
        },
      ]
      : []),
  ];

  const handleAddExpense = () => {
    setShowModal(true);
    setModalType('expense');
  };

  const summaryData = [
    { title: 'Total venta manual', value: formatCurrency(summary.manual_amount) },
    { title: 'Total venta por link', value: formatCurrency(summary.referral_amount) },
    { title: 'Comisión del vendedor', value: formatCurrency(summary.total_comission_amount) },
    { title: 'Egresos', value: formatCurrency(summary.transactions_summary.total_amount) },
    { title: 'Saldo del productor', value: formatCurrency(summary.balance) },
  ];

  return (
    <div className="flex flex-col gap-6 my-6">
      <div className="grid grid-cols-1 md:grid-cols-10 gap-4">
        <div
          className={`col-span-10 ${(role === 'ADMIN' || role === 'ROOT') ? 'md:col-span-10' : 'md:col-span-4'
          }`}
        >
          <AdminSectionTitle
            title={event?.name ?? 'Eventos'}
            subtitle={`Panel de ${summary.seller_name}`}
            mainButtons={mainButtons}
          />
        </div>
      </div>
      {(!isUserActive || !event?.seller_sales_enabled) && <div className='flex flex-col gap-4'>
        <p className='text-red-500'>
          {role === 'SELLER' ? 'No estás habilitado para vender entradas.' : 'Vendedor no habilitado para vender entradas.'}
        </p>
      </div>}
      <div className="flex flex-col gap-4 glass-surface">
        <h1 className="text-xl font-semibold">Balance</h1>
        <Summary summary={summaryData} />
      </div>

      <div className="flex flex-col gap-4 glass-surface">
        <h1 className="text-xl font-semibold">Resumen de entradas</h1>
        <TicketsSummaryTable summaries={summary.tickets_summary} />
      </div>

      {orders.length > 0 && <div className="flex flex-col gap-4 glass-surface">
        <h1 className="text-xl font-semibold">Entradas vendidas manuales</h1>
        <ApprovedOrdersTable orderItems={orders} />
      </div>}

      {(role === 'ADMIN' || role === 'ROOT') && (
        <div className="flex flex-col gap-4 glass-surface">
          <h1 className="text-xl font-semibold">Movimientos</h1>
          <TransactionsTable
            transactions={summary.transactions_summary.transactions}
            businessId={businessId}
            eventId={eventId}
            role={role}
            buttonAction={handleAddExpense}
          />
        </div>
      )}

      <CustomModal
        onRequestClose={() => setShowModal(false)}
        isOpen={showModal}
        hideCancelButton
        overflowVisible
      >
        {modalType === 'delete' && <DeleteSeller
          sellsCount={summary.total_items_count}
          userEventId={summary.user.current_event.id}
          businessId={businessId}
          eventId={eventId}
          onCancel={() => setShowModal(false)}
          onDeleteSuccess={handleDeleteSuccess}
        />}
        {modalType === 'expense' && <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <h1 className="text-xl font-medium">Registrar movimiento</h1>
            <p className="text-text-tertiary text-sm">
              Podrás agregar gastos a este vendedor
            </p>
          </div>
          <AddUserExpense
            businessId={businessId}
            eventId={eventId}
            sellerId={summary.seller_id}
            onSuccess={() => setShowModal(false)}
            type={EventTransactionType.SellerExpense}
          />
        </div>}
        {modalType === 'edit' && <div className="flex flex-col gap-4 overflow-y-visible">
          <div className="flex flex-col gap-2">
            <h1 className="text-xl font-medium">Editar vendedor</h1>
            <p className="text-text-tertiary text-sm">
              Podrás configurar la comisión de este vendedor
            </p>
          </div>
          <AddEditSellerToEvent
            businessId={businessId}
            eventId={eventId}
            onSuccess={handleEditUserSuccess}
            sellerToEdit={{
              userId: summaryToEdit?.user.current_event.id ?? '',
              comissionRate:
                summaryToEdit?.user.current_event.seller_comission_rate ?? 0,
              active: summaryToEdit?.user.current_event.active ?? false,
              email: summaryToEdit?.user.email ?? '',
            }}
            onClose={() => setShowModal(false)}
          />
        </div>}
      </CustomModal>
    </div>
  );
};

export default SellersSummaryDetails;
